import { useLocation } from 'react-router';
import type {
  ExternalLink as ExternalLinkType,
  InternalLink as InternalLinkType
} from 'types/capi/shared';

import AffiliateLinkClickEvent from '~/events/AffiliateLinkClickEvent';
import useEnv from '~/hooks/useEnv';
import useMarketingCookies from '~/hooks/useMarketingCookies';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import type { TrackedButtonClickName } from '~/services/gtm.client';
import wct from '~/services/wct.client';

import { getTrackedUrlAndClickRef } from '../../utils/tracking';
import Tracked from '../Tracked';

type Props = {
  preventPropagation?: boolean;
};

export const Internal = ({
  link,
  preventPropagation = false
}: { link: InternalLinkType } & Props) => {
  function handleClickInternalLink(e: React.SyntheticEvent) {
    if (preventPropagation) e.stopPropagation();
  }

  return (
    <Tracked.InternalLink
      className="inline font-semibold underline"
      variant="none"
      pagePosition="Body"
      name={link.text as TrackedButtonClickName}
      to={link.path}
      onClick={handleClickInternalLink}
    >
      {link.text}
    </Tracked.InternalLink>
  );
};

export const External = ({
  link,
  preventPropagation = false
}: { link: ExternalLinkType } & Props) => {
  const user = useUser();
  const env = useEnv();
  const marketingCookies = useMarketingCookies();
  const { clientBrowser, clientIp, isBot, session } = useRootData();
  const { pathname, search } = useLocation();

  const { url, clickRef } = getTrackedUrlAndClickRef(link, { user });

  const onClick = (e: React.SyntheticEvent) => {
    if (preventPropagation) e.stopPropagation();

    wct.fireEvent({
      clickRef,
      clientBrowser,
      clientIp,
      landingPage: marketingCookies.getLandingPage() ?? 'unknown',
      pagePath: `${env.SITE_BASE_URL}${pathname}${search}`,
      session,
      url
    });

    if (link.affiliate)
      new AffiliateLinkClickEvent({
        affiliateName: link.affiliate.name,
        clientIp,
        isBot,
        isReseller: link.affiliate.is_reseller,
        pagePosition: 'Body',
        retailerId: link.affiliate.id,
        user
      }).send();
  };

  return (
    <Tracked.ExternalLink
      href={url}
      variant="none"
      target="_blank"
      onClick={onClick}
      name={link.text as TrackedButtonClickName}
      rel="sponsored nofollow noopener"
      pagePosition="Body"
      className="inline font-semibold underline"
    >
      {link.text}
    </Tracked.ExternalLink>
  );
};

export default { External, Internal };
